import React, { FC, useState, useRef, useMemo, useCallback } from 'react';
import { ButtonDataStatus, Pages, Localization, SelectionHighlighted, TestIds } from 'utils';
import {
    WppTypography,
    WppIconDownload,
    WppSelect,
    WppListItem,
} from '@wppopen/components-library-react';
import { usePlotlyDataOptimizer, useScreenshot } from 'hooks';
import { ICreateScenario, legendTitleChanger, sideModalTitleChanger } from 'utils';
import { NotificationsSection } from '.';
import {
    ScreenshotWrapper,
    PlotlyComponent,
    Footer,
    SideModal,
    Header,
    Pill,
    SegmentControl,
} from 'components';
import styles from './CreateScenario.module.scss';
import { SelectionSection } from './SelectionSection/SelectionSection';
import { useDispatch, useSelector } from 'react-redux';
import { scenarioSlice, updatePlotlySelection } from 'Redux/createScenarioSlice';
import { plotlyControls } from './SchemaScenario';
import { RedirectButton } from 'components/RedirectButton/RedirectButton';

enum PLOTS_POSITION {
    FirstPlot = 'FIRST_PLOT',
    SecondPlot = 'SECOND_PLOT',
}

const {
    Main,
    MainInfo,
    MainInfoTitleHow,
    MainInfoWrapper,
    MainPaper,
    MainContent,
    MainPaperHeader,
    MainPaperHeaderDownload,
    MainPaperPillgroupWrapper,
    MainPaperPillgroup,
    MainPaperPillgroupHeader,
    MainPaperPillgroupSubcategory,
} = styles;

export const CreateScenario: FC<ICreateScenario> = ({ title, localization }) => {
    const {
        [localization]: { data, isDataLoading, plotlySelection, selectionState },
    } = useSelector(scenarioSlice);
    const dispatch = useDispatch();
    const ref = useRef(null);
    const { handleClickTakeScreenShot } = useScreenshot();
    const [showSideModal, setShowSidemodal] = useState<boolean>(false);
    const [isRunClicked, setIsRunClicked] = useState<boolean>(false);
    const { highlighted, per_anum } = plotlySelection;
    const subCategories = selectionState?.category;

    const handlerPlotSelection = useCallback(
        (key: string, value: string) => {
            dispatch(updatePlotlySelection({ localization, data: { value, key } }));
        },
        [dispatch, localization],
    );

    const handlerBooleanStatement = (key: ButtonDataStatus, value: boolean) => {
        if (key === ButtonDataStatus.IS_BUTTON_RUN) {
            setIsRunClicked(value);
        }
    };

    const dataGenerator = (position: PLOTS_POSITION) => {
        const highlightedSubcategory = data['Subcategory'][highlighted];
        const highlightedCategory = data[highlighted];

        switch (localization) {
            case Localization.US:
                if (position === 'FIRST_PLOT') {
                    return subCategories.includes(highlighted)
                        ? highlightedSubcategory[SelectionHighlighted.CATEGORY_SPEND][per_anum]
                        : highlightedCategory[SelectionHighlighted.CATEGORY_SPEND][per_anum];
                } else {
                    return subCategories.includes(highlighted)
                        ? highlightedSubcategory[SelectionHighlighted.DEMOGRAPHIC_POPULATION]
                        : highlightedCategory[SelectionHighlighted.DEMOGRAPHIC_POPULATION];
                }
            case Localization.UK:
                if (position === 'FIRST_PLOT') {
                    return subCategories.includes(highlighted)
                        ? highlightedSubcategory[SelectionHighlighted.CATEGORY_SPEND][per_anum]
                        : highlightedCategory[SelectionHighlighted.CATEGORY_SPEND][per_anum];
                } else {
                    return subCategories.includes(highlighted)
                        ? highlightedSubcategory[SelectionHighlighted.DEMOGRAPHIC_POPULATION]
                        : highlightedCategory[SelectionHighlighted.DEMOGRAPHIC_POPULATION];
                }
            default:
                return null;
        }
    };

    const firstPlot = usePlotlyDataOptimizer({
        data: dataGenerator(PLOTS_POSITION.FirstPlot),
        type: highlighted,
    });

    const secondPlot = usePlotlyDataOptimizer({
        data: dataGenerator(PLOTS_POSITION.SecondPlot),
        type: highlighted,
    });

    const CustomHeader = useMemo(
        () => (
            <>
                <WppTypography type="s-strong">Category Spend</WppTypography>
                <SegmentControl
                    value={per_anum}
                    handleChange={handlerPlotSelection}
                    propertyKey="per_anum"
                    dataTestId="SCENARIO.PLOTLY.SEGMENT"
                />
            </>
        ),
        [per_anum, handlerPlotSelection],
    );

    const highControls = plotlyControls[localization]['highlighted'];
    const isCategorySelectionShown =
        highlighted === highControls['Category'] ||
        highlighted === highControls['All Subcategories'] ||
        subCategories.includes(highlighted);

    const allHighlightedCases = [
        {
            label: SelectionHighlighted.TOTAL_DEFINED_SEGMENT,
            id: highControls[SelectionHighlighted.TOTAL_DEFINED_SEGMENT],
        },
        {
            label: SelectionHighlighted.SEX,
            id: highControls[SelectionHighlighted.SEX],
        },
        {
            label: SelectionHighlighted.AGE_GROUP,
            id: highControls[SelectionHighlighted.AGE_GROUP],
        },
        {
            label: SelectionHighlighted.ETHNIC_GROUP,
            id: highControls[SelectionHighlighted.ETHNIC_GROUP],
        },
        {
            label: SelectionHighlighted.CATEGORY,
            id: highControls[SelectionHighlighted.CATEGORY],
        },
    ];

    return (
        <div className={Main} ref={ref} data-testid="SCENARIO.PAGE.CONTAINER.MAIN">
            <ScreenshotWrapper>
                <div className={MainInfoWrapper} id="title">
                    <div className={MainInfo}>
                        <Header
                            title={title}
                            localization={localization}
                            isBackButtonShown={true}
                            flexDirection="column"
                        >
                            <RedirectButton />
                            <WppTypography
                                className={MainInfoTitleHow}
                                type="s-body"
                                onClick={() => setShowSidemodal(true)}
                                data-testid={TestIds.scenario.actions.howToUseButton}
                            >
                                How to use
                            </WppTypography>
                        </Header>
                    </div>
                    <SelectionSection
                        handlerBooleanStatement={handlerBooleanStatement}
                        isRunClicked={isRunClicked}
                        localization={localization}
                        testId={TestIds.scenario.selection}
                    />
                </div>
                <div className={MainContent}>
                    <div className={MainPaper}>
                        <div className={MainPaperHeader}>
                            <NotificationsSection
                                sex={selectionState?.sex}
                                age_group={selectionState.age_group}
                                heritage={selectionState.heritage}
                                category={selectionState.category}
                                demographic={selectionState.demographic}
                                geoMapState={selectionState.geoMapSelection}
                                regeneratedData={isDataLoading}
                            />
                            <WppIconDownload
                                className={MainPaperHeaderDownload}
                                onClick={() =>
                                    handleClickTakeScreenShot(
                                        ref.current,
                                        ['runScenarioButton', 'cancelButton'],
                                        legendTitleChanger(highlighted),
                                    )
                                }
                                id="download-icon"
                                style={{ display: isDataLoading ? 'none' : 'block' }}
                                data-testid="SCENARIO.PLOT.DOWNLOAD.BUTTON"
                            />
                        </div>

                        <div className={MainPaperPillgroupWrapper}>
                            <div className={MainPaperPillgroup}>
                                <div className={MainPaperPillgroupHeader}>
                                    <Pill
                                        value={highlighted}
                                        handleChange={handlerPlotSelection}
                                        pillName="highlighted"
                                        isDisabled={isDataLoading}
                                        cases={allHighlightedCases}
                                        dataTestId={TestIds.scenario.plotPills}
                                    />

                                    {isCategorySelectionShown && (
                                        <div className={MainPaperPillgroupSubcategory}>
                                            <WppSelect
                                                onWppChange={(e) =>
                                                    handlerPlotSelection(
                                                        'highlighted',
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Make selection"
                                                value={highlighted}
                                                key="regionsSelection"
                                                size="s"
                                                style={{ width: 192, zIndex: 1 }}
                                                disabled={isDataLoading}
                                            >
                                                <WppListItem
                                                    key={highControls[SelectionHighlighted.NONE]}
                                                    value={highControls[SelectionHighlighted.NONE]}
                                                    id={highControls[SelectionHighlighted.NONE]}
                                                >
                                                    <p slot="label">All Categories</p>
                                                </WppListItem>
                                                {subCategories?.map((element: string) => (
                                                    <WppListItem
                                                        key={element}
                                                        value={element}
                                                        id={element}
                                                    >
                                                        <p slot="label">{element}</p>
                                                    </WppListItem>
                                                ))}
                                            </WppSelect>
                                        </div>
                                    )}
                                </div>
                                <PlotlyComponent
                                    plotlyData={[
                                        {
                                            ...firstPlot,
                                            header: CustomHeader,
                                        },
                                        {
                                            ...secondPlot,
                                            header: (
                                                <WppTypography type="s-strong">
                                                    Demographic Population
                                                </WppTypography>
                                            ),
                                        },
                                    ]}
                                    plotHeight={520}
                                    legendTitle={legendTitleChanger(highlighted)}
                                    legendPosition="right"
                                    sideModalTitle={sideModalTitleChanger(highlighted)}
                                    localization={localization}
                                    isLoading={isDataLoading}
                                    page={Pages.CREATE_SCENARIO}
                                />
                            </div>
                        </div>
                    </div>
                    <Footer localization={localization} />
                </div>
            </ScreenshotWrapper>
            <SideModal
                title="How to use"
                isSidebarShown={showSideModal}
                setIsSidebarShown={setShowSidemodal}
                localization={localization}
            />
        </div>
    );
};
